import React, { useState, useEffect, Fragment } from 'react';
import {
  Alert,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material';

// COMPONENTS
import CustomModal from '../CustomModal';
import HelpTooltip from '../HelpTooltip';

// I18N
import UsersFormattedMessages from '../../../messages/account/UsersFormattedMessages';

// NETWORK
import useUsersRequest from '../../../requests/UsersRequest';
import Loader from '../Loader';
import EmailAutocomplete from '../EmailAutoComplete';

const ManageDedicatedNumbers = (props) => {
  const { errorHandler, navigate, user, page, callBack } = props;

  const [error, setError] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [available, setAvailable] = useState([]); // list of numbers that are available
  const [desired, setDesired] = useState([]); // list of numbers to be added
  const [dedicated, setDedicated] = useState(null); // dedicated numbers
  const [undesired, setUndesired] = useState({ numbers: [] }); // list of numbers to be removed
  const [edits, setEdits] = useState({});
  const [isSearching, setIsSearching] = useState(false); // should toggle while waiting for twilio response.
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [legacyReleaseCheck, setLegacyReleaseCheck] = useState(false);
  const [selected, setSelected] = useState(0); // number of numbers selected for provisioning?
  const [campaigns, setCampaigns] = useState(null);
  const [campaignEdits, setCampaignEdits] = useState({});
  const [selectedGateway, setSelectedGateway] = useState('Twilio');
  const [selectedTemplateDevice, setSelectedTemplateDevice] = useState('6601');
  const [templateDeviceValue, setTemplateDeviceValue] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransferredNumber, setSelectedTransferredNumber] = useState(
    {}
  );
  const [isTransferring, setIsTransferring] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [input, setInput] = useState('');
  const [isChangingCampaign, setIsChangingCampaign] = useState(false);
  const {
    getDedicatedNumbers,
    getCustomerCampaignData,
    transferDedicatedNumber,
    searchForUser,
    searchAvailableNumbers,
    changeCampaignForNumbers,
    provisionNumbers,
    releaseTwilioNumbers,
  } = useUsersRequest();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dedicatedNumbers = await getDedicatedNumbers(
          user.customer_user_id,
          errorHandler
        );
        const campaigns = await getCustomerCampaignData(
          user.customer_id,
          errorHandler
        );

        setDedicated(dedicatedNumbers);
        setCampaigns(campaigns.currentCampaigns);
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchData();
  }, [user.customer_user_id, user.customer_id, errorHandler]);

  const generateErrorText = () => {
    if (!hasError) return null;

    return <Alert severity="error">{error}</Alert>;
  };

  const handleChange = (e, device_id = null) => {
    const { name, value, type, checked } = e.target;
    const id = device_id;
    let newEdits = { ...edits };
    let newDesired = [...desired];
    let newUndesired = { ...undesired };

    if (type === 'checkbox') {
      if (name === 'provision') {
        if (checked) {
          newDesired.push(value);
        } else {
          newDesired = newDesired.filter((num) => num !== value);
        }
      }

      if (name === 'release') {
        const udItem = { device_id: id, phoneNumber: value };
        if (checked) {
          newUndesired.numbers.push(udItem);
        } else {
          newUndesired.numbers = newUndesired.numbers.filter(
            (num) => num.device_id !== id
          );
        }
      }
    } else if (name === 'campaignChange') {
      const index = dedicated.findIndex((element) => {
        if (element.device_id === id) {
          if (value === 'N/A' && element.campaignCode !== undefined) {
            return true; // user selects no campaign when there was one selected.
          } else if (value !== 'N/A' && value !== element.campaignCode) {
            return true; // user selects a code where previously there was none
          }
        }
      });

      let newCampaignEdits = campaignEdits;
      if (index !== -1) newCampaignEdits[id] = value;
      else delete newCampaignEdits[id];

      console.log(newCampaignEdits);
      setCampaignEdits(newCampaignEdits);
    }

    newEdits[name] = value;

    setEdits(newEdits);
    setDesired(newDesired);
    setUndesired(newUndesired);
    setHasError(false);
    setError('');
  };

  const validateForm = () => {
    let isValidForm = false;
    let errText = '';
    const { prefix } = edits;

    // prefix must be 3 digit number
    if (edits.prefix && edits.prefix.length === 3 && !isNaN(edits.prefix)) {
      isValidForm = true;

      // number filter must be more than one character
    } else if (edits.numberContains && edits.numberContains.length > 1) {
      isValidForm = true;
    } else {
      errText = 'Requires a 3-digit prefix or number filter thats 2+ digits.';
    }

    if (!errText) isValidForm = true;

    setHasError(!isValidForm);
    setError(errText);

    return {
      isValidForm: isValidForm,
      formError: errText,
    };
  };

  const generateLander = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          marginBottom: 2,
        }}
      >
        {generateReleaseButton()}
      </Box>
    );
  };

  const generateSearchButton = () => {
    if (isSearching) return <Typography>Searching...</Typography>;

    return (
      <Button
        type="button"
        variant="outlined"
        onClick={handleSearch}
        color="secondary"
      >
        Search for available numbers
      </Button>
    );
  };

  const generateProvisionButton = () => {
    if (isProvisioning) return <Typography>Provisioning...</Typography>;

    if (desired.length <= 0) return;

    return (
      <Button
        type="button"
        onClick={handleProvision}
        variant="contained"
        color="secondary"
      >
        Provision selected
      </Button>
    );
  };

  const generateReleaseButton = () => {
    if (isReleasing) return <Typography>Releasing...</Typography>;

    const nums = undesired.numbers;
    if ((nums ? nums.length : 0) <= 0) return;

    return (
      <Button
        type="button"
        onClick={handleRelease}
        variant="contained"
        color="secondary"
      >
        Release selected
      </Button>
    );
  };

  const generateWarningModal = () => {
    return (
      <CustomModal
        title={UsersFormattedMessages.modalWarning}
        open={legacyReleaseCheck}
        onClose={() => {
          setLegacyReleaseCheck(false);
          setIsReleasing(false);
        }}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Typography>{UsersFormattedMessages.modalBody}</Typography>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setLegacyReleaseCheck(false);
              setIsReleasing(false);
            }}
          >
            {UsersFormattedMessages.modalCancel}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleRelease(false)}
          >
            {UsersFormattedMessages.modalYes}
          </Button>
        </Box>
      </CustomModal>
    );
  };

  const handleTransfer = async () => {
    setIsTransferring(true);

    if (!selectedOption || !selectedOption.value) {
      return setHasError(true);
    }

    try {
      const result = await transferDedicatedNumber(
        selectedTransferredNumber.device_id,
        selectedTransferredNumber.reply_tn,
        selectedOption.value,
        errorHandler
      );

      if (!result.error) {
        setIsLoading(true);
        setShowTransferModal(false);
        setHasError(false);
        setIsTransferring(false);
        let url = window.location.href;
        if (url.includes('admin')) {
          callBack();
        } else if (url.includes('users')) {
          navigate('/admin/customers/' + user.customer_id, {
            state: { isTransferDedicatedNumber: true },
          });
        }
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleUserSearchChange = (event, selectedOption) => {
    setSelectedOption(selectedOption);
    setInput('');
  };

  const handleOpenTransferDNModal = async (item) => {
    const searchBody = {
      transferDNDropdown: true,
      fromUser: user.customer_user_id,
    };

    try {
      let data = await searchForUser(searchBody, errorHandler);
      data = data.map((user) => {
        return { value: user.customer_user_id, label: user.user_email };
      });

      setShowTransferModal(true);
      setSelectedTransferredNumber(item);
      setHasError(false);
      setSelectedOption(null);
      setUsersList(data);
      setInput('');
    } catch (error) {
      errorHandler(error);
    }
  };

  const generateTransferModal = () => {
    return (
      <CustomModal
        open={showTransferModal}
        title="Transfer Number"
        onClose={() => {
          setShowTransferModal(false);
        }}
      >
        <Typography gutterBottom>
          Enter the details to transfer {selectedTransferredNumber?.reply_tn}
        </Typography>

        <Stack spacing={1} sx={{ marginBottom: 2 }}>
          <InputLabel sx={{ minWidth: 160 }}>Transfer to</InputLabel>
          <EmailAutocomplete
            autoFocus
            name="transferTo"
            placeholder="Enter a user email address"
            options={usersList}
            value={selectedOption}
            onChange={handleUserSearchChange}
            inputValue={input}
            onInputChange={(event, newInputValue) => {
              setInput(newInputValue);
            }}
            size="small"
            fullWidth
          />
        </Stack>

        {generateErrorText()}

        <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isTransferring ? (
            <CircularProgress />
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedTransferredNumber({});
                  setHasError(false);
                  setSelectedOption(null);
                }}
              >
                {UsersFormattedMessages.modalCancel}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleTransfer()}
              >
                Confirm Transfer
              </Button>
            </Box>
          )}
        </Box>
      </CustomModal>
    );
  };

  const handleSearch = async () => {
    const result = validateForm();
    if (!result.isValidForm) {
      generateErrorText();
      return;
    }

    const data = {
      prefix: edits.prefix,
      country: edits.country || 'US',
      gateway: selectedGateway,
      numberContains: edits.numberContains,
    };

    setIsSearching(true);
    try {
      const newNumbersObject = await searchAvailableNumbers(data, errorHandler);

      if (newNumbersObject === 'undefined') {
        console.log('Error retrieving numbers from Twilio');
        callBack(true);
      } else {
        setAvailable(newNumbersObject);
        setDesired([]);
        clearCheckboxes('provision');
        setIsSearching(false);
      }
    } catch (error) {
      errorHandler(error);
      setIsSearching(false);
    }
  };

  const clearCheckboxes = (name) => {
    const elements = document.getElementsByClassName(
      'form-check-input position-static'
    );
    for (let i = 0, len = elements.length; i < len; i++) {
      if ((elements[i] ? elements[i].name : null) === name)
        elements[i].checked = false;
    }
  };

  const handleChangeCampaignForNumber = async () => {
    setIsChangingCampaign(true);
    try {
      const response = await changeCampaignForNumbers(
        campaignEdits,
        user.customer_user_id,
        errorHandler
      );

      const dedicatedNumbers = await getDedicatedNumbers(
        user.customer_user_id,
        errorHandler
      );
      setCampaignEdits({});
      setDedicated(dedicatedNumbers);
      setIsChangingCampaign(false);
    } catch (error) {
      errorHandler(error);
      setIsChangingCampaign(false);
    }
  };

  const handleProvision = async () => {
    setIsProvisioning(true);
    try {
      const data = {
        templateDeviceId:
          selectedTemplateDevice ||
          getTemplateDeviceOptions(selectedGateway)?.id,
        numbers: desired,
        customerUserId: user.customer_user_id,
        customerId: user.customer_id,
        campaignCode: edits.campaignCode,
        gateway: selectedGateway,
      };

      const response = await provisionNumbers(data, errorHandler);
      const dedicatedNumbers = await getDedicatedNumbers(
        user.customer_user_id,
        errorHandler
      );

      if (edits?.campaignCode) delete edits.campaignCode;

      clearCheckboxes('provision');
      setIsProvisioning(false);

      setDedicated(dedicatedNumbers);
      setAvailable([]);
      setDesired([]);
      setEdits(edits);
    } catch (error) {
      errorHandler(error);
      setIsProvisioning(false);
    }
  };

  const handleRelease = async (checkFlag) => {
    setIsReleasing(true);
    setLegacyReleaseCheck(false);
    try {
      if (checkFlag) {
        for (let i = 0; i < undesired.numbers.length; i++) {
          const undesiredNumber = undesired.numbers[i];
          const deviceIndex = dedicated.findIndex(
            (element) => element.device_id === undesiredNumber.device_id
          );

          if (deviceIndex !== -1) {
            const legacyCheck =
              dedicated[deviceIndex].device_name?.indexOf('AWS');
            if (legacyCheck === -1 || legacyCheck === undefined) {
              return setLegacyReleaseCheck(true);
            }
          }
        }
      }

      await releaseTwilioNumbers(undesired, errorHandler);
      const dedicatedNumbers = await getDedicatedNumbers(
        user.customer_user_id,
        errorHandler
      );

      const ud = { numbers: [] };

      clearCheckboxes('release');
      setIsReleasing(false);

      setDedicated(dedicatedNumbers);
      setUndesired(ud);
    } catch (error) {
      errorHandler(error);
      setIsReleasing(false);
    }
  };

  const generateAvailableTable = () => {
    if (available === undefined || !available || available.length === 0) return;

    const htmlRows = available.map((item, i) => {
      const locality =
        item.locality.charAt(0).toUpperCase() +
        item.locality.slice(1).toLowerCase();
      return (
        <TableRow key={i}>
          <TableCell>{item.phoneNumber}</TableCell>
          <TableCell>{locality}</TableCell>
          <TableCell>
            <FormControlLabel
              control={
                <Checkbox
                  type="checkbox"
                  value={item.phoneNumber}
                  name="provision"
                  className="provision-checks"
                  onChange={handleChange}
                />
              }
              label=""
            />
          </TableCell>
        </TableRow>
      );
    });

    return (
      <Box>
        {generateCampaignDropdown()}

        <Box sx={{ display: 'flex', justifyContent: 'end', marginBottom: 1 }}>
          {generateProvisionButton()}
        </Box>
        <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ whiteSpace: 'nowrap' }}>
              <TableRow>
                <TableCell>Phone Number</TableCell>
                <TableCell>Locality</TableCell>
                <TableCell>Provision?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{htmlRows}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const generateDedicatedNumbersTable = () => {
    const changeableRoutes = ['Twilio'];

    if (!dedicated) return <Loader />;
    if (dedicated.length === 0)
      return <Typography gutterBottom>No result found</Typography>;

    const differentOptions = {
      default: [
        <MenuItem key={'N/A'} value={'N/A'}>
          {'N/A'}
        </MenuItem>,
      ],
    };

    campaigns.forEach(({ campaign_code, campaign_type }, i) => {
      if (!differentOptions[campaign_type]) {
        differentOptions[campaign_type] = [
          <MenuItem key={'N/A'} value={'N/A'}>
            {'N/A'}
          </MenuItem>,
        ];
      }

      differentOptions[campaign_type].push(
        <MenuItem key={i} value={campaign_code}>
          {campaign_code}
        </MenuItem>
      );
    });

    const htmlRows = dedicated.map((item, i) => {
      const { device_name, device_id, route_name, campaignCode, reply_tn } =
        item;
      const splitArray = route_name?.split(' ');
      const routeType = splitArray?.length ? splitArray[0] : 'default';
      const isChangeable = changeableRoutes.includes(routeType);

      let campaignContent = (
        <Select
          labelId="campaign-label"
          defaultValue={campaignCode || 'N/A'}
          onChange={(e) => handleChange(e, device_id)}
          name="campaignChange"
          disabled={!isChangeable}
          size="small"
        >
          {differentOptions[routeType]}
        </Select>
      );

      if (
        !campaigns ||
        campaigns.findIndex(
          (e) => e.campaign_code === campaignCode || campaignCode === 'N/A'
        ) === -1
      ) {
        campaignContent = campaignCode || 'N/A';
      }

      return (
        <TableRow key={i}>
          <TableCell>{device_name}</TableCell>
          <TableCell>{campaignContent}</TableCell>
          <TableCell>{route_name}</TableCell>
          <TableCell>{reply_tn}</TableCell>
          <TableCell>
            <Checkbox
              type="checkbox"
              value={reply_tn}
              name="release"
              onChange={(e) => handleChange(e, device_id)}
              size="small"
            />
          </TableCell>
          <TableCell>
            <Button
              type="button"
              onClick={() => handleOpenTransferDNModal(item)}
              variant="contained"
              color="secondary"
            >
              Transfer
            </Button>
          </TableCell>
        </TableRow>
      );
    });

    let {
      campaign,
      deviceName,
      release,
      gateway,
      dedicatedNumber,
      saveChanges,
      saving,
    } = UsersFormattedMessages;

    let button = null;
    if (isChangingCampaign) {
      button = <Typography>{saving}</Typography>;
    } else if (campaignEdits && Object.keys(campaignEdits).length !== 0) {
      button = (
        <Button
          type="button"
          onClick={handleChangeCampaignForNumber}
          variant="contained"
          color="secondary"
        >
          {saveChanges}
        </Button>
      );
    }

    return (
      <Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ whiteSpace: 'nowrap' }}>
              <TableRow>
                <TableCell>{deviceName}</TableCell>
                <TableCell>{campaign}</TableCell>
                <TableCell>{gateway}</TableCell>
                <TableCell>{dedicatedNumber}</TableCell>
                <TableCell>{release}</TableCell>
                <TableCell>Transfer To</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{htmlRows}</TableBody>
          </Table>
        </TableContainer>
        {button}
      </Box>
    );
  };

  const handleGatewayDropdown = (event) => {
    const selectedValue = event.target.value;
    const selectedTemplateObject =
      getTemplateDeviceOptions(selectedValue) || {};
    const newEdits = { ...edits };

    if (selectedValue === 'Telnyx') newEdits.country = 'US';

    setSelectedGateway(selectedValue);
    setSelectedTemplateDevice(selectedTemplateObject?.id);
    setTemplateDeviceValue('');
    setAvailable([]);
    setDesired([]);
    setEdits(newEdits);
    clearCheckboxes('provision');
  };

  const handleTemplateDeviceDropdown = (event) => {
    setSelectedTemplateDevice(event.target.value);
    setTemplateDeviceValue('');
  };

  const getTemplateDeviceOptions = (selectedValue) => {
    if (selectedValue === 'Twilio')
      return {
        name: 'Twilio USA #6601 TxtMe (Template)',
        id: '6601',
      };
    else if (selectedValue === 'Telnyx')
      return {
        name: 'Telnyx #7001 TxtMe (Template)',
        id: '7001',
      };
    else if (selectedValue === 'Bandwidth') {
      if (process.env.REACT_APP_ENV === 'production') {
        return {
          name: 'Bandwidth #8002 TxtMe (Prod Template)',
          id: '8002',
        };
      } else {
        return {
          name: 'Bandwidth #8001 TxtMe (Dev Template)',
          id: '8001',
        };
      }
    }
  };

  const generateDropdowns = () => {
    const options = [];

    if (selectedGateway === 'Twilio') {
      options.push(
        <MenuItem key={'6601'} value={'6601'}>
          {'Twilio USA #6601 TxtMe (Template)'}
        </MenuItem>
      );
    } else if (selectedGateway === 'Telnyx') {
      options.push(
        <MenuItem key={'7001'} value={'7001'}>
          {'Telnyx #7001 TxtMe (Template)'}
        </MenuItem>
      );
    } else if (selectedGateway === 'Bandwidth') {
      options.push(
        <MenuItem key={'8001'} value={'8001'}>
          {'Bandwidth #8001 TxtMe (Dev Template)'}
        </MenuItem>
      );
      options.push(
        <MenuItem key={'8002'} value={'8002'}>
          {'Bandwidth #8002 TxtMe (Prod Template)'}
        </MenuItem>
      );
    }

    return (
      <>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
        >
          <InputLabel sx={{ minWidth: 160 }}>Gateway</InputLabel>
          <Select
            labelId="gateway-label"
            id="gateway"
            value={selectedGateway}
            onChange={handleGatewayDropdown}
            fullWidth
            size="small"
          >
            <MenuItem value="Twilio">Twilio</MenuItem>
            <MenuItem value="Telnyx">Telnyx</MenuItem>
            <MenuItem value="Bandwidth">Bandwidth</MenuItem>
          </Select>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
        >
          <InputLabel sx={{ minWidth: 160 }}>Template Device</InputLabel>
          <Select
            labelId="template-device-label"
            id="template-device"
            value={selectedTemplateDevice}
            onChange={handleTemplateDeviceDropdown}
            fullWidth
            size="small"
          >
            {options}
          </Select>
        </Stack>
      </>
    );
  };

  const generateCampaignDropdown = () => {
    if (!campaigns) return null;

    const options = [
      <MenuItem key={'None'} value={''}>
        {'None'}
      </MenuItem>,
    ];

    for (let i = 0; i < campaigns.length; i++) {
      if (selectedGateway === campaigns[i].campaign_type) {
        options.push(
          <MenuItem key={i} value={campaigns[i].campaign_code}>
            {campaigns[i].campaign_code}
          </MenuItem>
        );
      }
    }

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
        maxWidth={800}
        marginTop={4}
        marginBottom={1}
      >
        <InputLabel sx={{ minWidth: 200 }}>
          Provision against campaign
        </InputLabel>
        <Select
          labelId="campaign-label"
          id="campaign"
          value={edits.campaignCode || ''}
          onChange={handleChange}
          name="campaignCode"
          fullWidth
          size="small"
        >
          {options}
        </Select>
      </Stack>
    );
  };

  const generateTextFormGroup = (label, controlId, isRequired, setType) => {
    let type = 'text';
    if (setType) type = setType;

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
      >
        <InputLabel sx={{ minWidth: 160 }} required={isRequired || true}>
          {label}
        </InputLabel>
        <TextField
          required={isRequired || true}
          name={controlId}
          type={type}
          onChange={handleChange}
          fullWidth
          size="small"
        />
      </Stack>
    );
  };

  const generateNumberContainsFormGroup = () => {
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
      >
        <InputLabel sx={{ minWidth: 160, display: 'flex' }} required>
          {UsersFormattedMessages.numberContains}
          <HelpTooltip
            message={
              <Stack>
                <Typography>
                  {UsersFormattedMessages.numberContainsTooltip}
                </Typography>
              </Stack>
            }
          />
        </InputLabel>

        <TextField
          required
          fullWidth
          name="numberContains"
          value={edits.numberContains || ''}
          onChange={handleChange}
          size="small"
          error={hasError}
          helperText={error}
        />
      </Stack>
    );
  };

  const generateCountryPicker = () => {
    const includesCanada = selectedGateway !== 'Telnyx';

    const options = [
      <MenuItem key={'US'} value={'US'}>
        {'United States'}
      </MenuItem>,
    ];

    if (includesCanada) {
      options.push(
        <MenuItem key={'CA'} value={'CA'}>
          {'Canada'}
        </MenuItem>
      );
    }

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ md: 'center' }}
      >
        <InputLabel sx={{ minWidth: 160 }}>Country</InputLabel>
        <Select
          labelId="country-label"
          id="country"
          value={edits.country || 'US'}
          onChange={handleChange}
          name="country"
          disabled={!includesCanada}
          fullWidth
          size="small"
        >
          {options}
        </Select>
      </Stack>
    );
  };

  const generateForm = () => {
    return (
      <Box component="form">
        <Box sx={{ marginBottom: 4 }}>{generateDedicatedNumbersTable()}</Box>
        <Stack gap={2} maxWidth={800}>
          {generateDropdowns()}
          {generateCountryPicker()}
          {generateTextFormGroup('Prefix', 'prefix', true)}
          {generateNumberContainsFormGroup()}
          {!showTransferModal ? generateErrorText() : null}
        </Stack>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginY: 4,
          }}
        >
          {generateSearchButton()}
        </Box>
        {generateAvailableTable()}
      </Box>
    );
  };

  return (
    <Fragment>
      {generateLander()}
      {generateWarningModal()}
      {generateTransferModal()}
      {generateForm()}
    </Fragment>
  );
};

export default ManageDedicatedNumbers;
